import React, { useState, useEffect, useCallback } from 'react';
import TablutBoard from './TablutBoard';
import {
  TablutGameState,
  TablutMove,
  Position,
  GameMode,
  Difficulty,
  TABLUT_PIECE_EMOJIS,
  TABLUT_CONFIG
} from '../types/game';
import {
  createInitialGameState,
  selectPiece,
  deselectPiece,
  makeMove,
  undoMove,
  getAllPossibleMoves,
  checkGameEnd
} from '../utils/tablut';
import {
  getTablutAIDecision,
  simulateTablutAIThinking
} from '../utils/tablutAI';
import GameShareButton from './GameShareButton';

interface TablutGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const TablutGame: React.FC<TablutGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBackToMenu,
  onBackToModeSelect
}) => {
  const [gameState, setGameState] = useState<TablutGameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );

  const [showWinDialog, setShowWinDialog] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // AI移动逻辑
  const makeAIMove = useCallback(async () => {
    // 确保只有攻击方回合才执行AI
    if (gameMode !== 'pvc' || gameState.currentPlayer === 'defender' || gameState.status !== 'playing' || isThinking) {
      return;
    }

    setIsThinking(true);

    try {
      // 获取AI决策（使用优化的AI算法）
      const aiMove = getTablutAIDecision(gameState, difficulty);

      if (aiMove) {
        // 模拟AI思考时间（使用优化后的思考时间）
        await simulateTablutAIThinking(difficulty);

        // 执行移动
        setGameState(prevState => makeMove(prevState, aiMove));
      } else {
        // 如果没有可用移动，直接切换回合
        console.warn('AI没有找到可用移动');
        setGameState(prevState => ({
          ...prevState,
          currentPlayer: 'defender',
          status: 'finished',
          winner: 'defender'
        }));
      }

      // AI移动完成后重置思考状态
      setIsThinking(false);
    } catch (error) {
      console.error('AI移动出错:', error);
      // 出错时重置思考状态
      setIsThinking(false);
    }
  }, [gameMode, difficulty, gameState, isThinking]);

  // 处理格子点击
  const handleCellClick = useCallback((row: number, col: number) => {
    if (gameState.status !== 'playing' || isThinking) return;

    const piece = gameState.board[row][col];

    if (gameState.selectedPiece) {
      // 已经选中了棋子，尝试移动
      const isValidMovePosition = gameState.possibleMoves?.some(
        move => move.to.row === row && move.to.col === col
      );

      if (isValidMovePosition) {
        // 执行移动
        const move: TablutMove = {
          player: gameState.currentPlayer,
          from: gameState.selectedPiece,
          to: { row, col },
          timestamp: Date.now()
        };

        const newGameState = makeMove(gameState, move);
        setGameState(newGameState);
      } else if (piece && piece.player === gameState.currentPlayer) {
        // 选择新的棋子
        setGameState(prevState => selectPiece(prevState, { row, col }));
      } else {
        // 取消选择
        setGameState(prevState => deselectPiece(prevState));
      }
    } else {
      // 还没有选中棋子，尝试选择
      if (piece && piece.player === gameState.currentPlayer) {
        setGameState(prevState => selectPiece(prevState, { row, col }));
      }
    }
  }, [gameState, isThinking]);

  // 检查游戏结束
  useEffect(() => {
    if (gameState.status === 'finished' && !showWinDialog && !dialogManuallyClosed) {
      setShowWinDialog(true);
    }
  }, [gameState.status, showWinDialog, dialogManuallyClosed]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing');
  }, [gameState.gameHistory.length, gameState.status]);

  // AI回合处理
  useEffect(() => {
    // 使用超时确保在下一个渲染周期执行AI移动
    const timeoutId = setTimeout(() => {
      if (gameMode === 'pvc' && gameState.currentPlayer === 'attacker' && gameState.status === 'playing' && !isThinking) {
        makeAIMove();
      }
    }, 100); // 100ms延迟确保状态稳定

    return () => clearTimeout(timeoutId);
  }, [gameState.currentPlayer, gameState.status, gameMode, makeAIMove, isThinking]);

  // 重新开始游戏
  const handleRestart = () => {
    setGameState(createInitialGameState(gameMode, difficulty));
    setShowWinDialog(false);
    setDialogManuallyClosed(false);
    setIsThinking(false);
    setCanUndo(false);
  };

  // 悔棋功能
  const handleUndo = () => {
    if (gameState.gameHistory.length > 0 && !isThinking && gameState.status === 'playing') {
      const newState = undoMove(gameState);
      setGameState(newState);
      setCanUndo(newState.gameHistory.length > 0);
    }
  };

  // 返回菜单
  const handleBackToMenu = () => {
    if (onBackToMenu) {
      onBackToMenu();
    }
  };

  // 获取当前玩家名称
  const getCurrentPlayerName = () => {
    return gameState.currentPlayer === 'defender' ? '护王方' : '攻击方';
  };

  // 获取游戏状态描述
  const getGameStatusText = () => {
    if (gameState.status === 'finished') {
      if (gameState.winner) {
        if (gameState.kingEscaped) {
          return `${gameState.winner === 'defender' ? '护王方' : '攻击方'}获胜（国王成功逃脱）`;
        } else if (gameState.kingCaptured) {
          return `${gameState.winner === 'defender' ? '护王方' : '攻击方'}获胜（国王被包围）`;
        } else {
          return `${gameState.winner === 'defender' ? '护王方' : '攻击方'}获胜`;
        }
      }
    } else {
      return getCurrentPlayerName() + '回合';
    }
  };

  // 获取回合数
  const getMoveCount = () => {
    return Math.floor(gameState.totalMoves / 2) + 1;
  };

  // 获取被吃掉的棋子数量
  const getCapturedPiecesCount = () => {
    return {
      defender: gameState.capturedPieces.attacker.length,
      attacker: gameState.capturedPieces.defender.length
    };
  };

  // 分享功能
  const handleShare = async () => {
    const shareData = {
      title: '萨米板棋 - 北欧传统战略游戏',
      text: `我正在玩萨米板棋！这是一个非常有趣的北欧传统棋类游戏，${gameMode === 'pvp' ? '双人对战' : `人机对战(${difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'})`}模式。快来挑战一下吧！`,
      url: window.location.origin + '/tablut'
    };

    try {
      if (navigator.share && /mobile|android|iphone|ipad|ipod/i.test(navigator.userAgent)) {
        // 移动设备使用原生分享API
        await navigator.share(shareData);
      } else {
        // 桌面设备复制链接到剪贴板
        await navigator.clipboard.writeText(shareData.url);
        alert('游戏链接已复制到剪贴板！');
      }
    } catch (err) {
      console.error('分享失败:', err);
      alert('分享失败，请手动复制游戏链接。');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🏰</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent sm:mb-1">
                萨米板棋
              </h1>
              <span className="text-2xl">👑</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}

            {/* 返回模式选择按钮 */}
            {onBackToModeSelect && (
              <button
                onClick={onBackToModeSelect}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={!canUndo || isThinking}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                canUndo && !isThinking
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title="游戏规则"
            >
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="tablut" />
          </div>
        </div>
      </div>

      {/* 游戏主体 */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl h-full flex items-center justify-center">
          <div className="w-full aspect-square max-w-[700px]">
            <TablutBoard
              board={gameState.board}
              selectedPiece={gameState.selectedPiece}
              possibleMoves={gameState.possibleMoves}
              kingPosition={gameState.kingPosition}
              onSelectPosition={handleCellClick}
              isAIThinking={isThinking}
            />
          </div>
        </div>
      </div>

      {/* 获胜对话框 */}
      {showWinDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full animate-bounce">
            <div className="text-center">
              <div className="text-6xl mb-4">
                {gameState.winner === 'defender' ? '👑' : '⚔️'}
              </div>
              <h2 className="text-2xl font-bold mb-4 text-gray-800">
                {gameState.winner === 'defender' ? '护王方获胜！' : '攻击方获胜！'}
              </h2>
              <p className="text-gray-600 mb-6">
                {gameState.kingEscaped ? (
                  <>国王成功逃脱到角落，护王方赢得了胜利！</>
                ) : gameState.kingCaptured ? (
                  <>国王被四面围住，攻击方赢得了胜利！</>
                ) : (
                  <>{gameState.winner === 'defender' ? '护王方' : '攻击方'}赢得了胜利！</>
                )}
              </p>

              <div className="flex space-x-3 justify-center">
                <button
                  onClick={handleRestart}
                  className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
                >
                  再来一局
                </button>
                <button
                  onClick={() => {
                    setShowWinDialog(false);
                    setDialogManuallyClosed(true);
                  }}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  继续查看
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <div className="text-center mb-6 pr-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">萨米板棋游戏规则</h2>
              <div className="text-6xl mb-4">🏰</div>
            </div>

            <div className="space-y-4 text-gray-700">
              {/* 游戏简介 */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-blue-800">📖 游戏简介</h3>
                <p className="text-sm leading-relaxed">
                  萨米板棋（Tablut）是源自北欧萨米人的传统棋类游戏，距今已有1500多年历史。
                  游戏模拟了国王与卫士对抗入侵攻击者的战斗，是一个不对称的战略游戏。
                </p>
              </div>

              {/* 游戏设置 */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-green-800">🎯 游戏设置</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>棋盘：</strong>9×9 方格棋盘</li>
                  <li><strong>护王方：</strong>1个国王 👑 + 8个卫士 🛡️</li>
                  <li><strong>攻击方：</strong>16个攻击者 ⚔️</li>
                  <li><strong>王座：</strong>棋盘中心位置（特殊标记 🏰）</li>
                  <li><strong>逃脱点：</strong>四个角落（金色圆圈标记）</li>
                </ul>
              </div>

              {/* 初始布局 */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-yellow-800">♟️ 初始布局</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>国王：</strong>放置在棋盘中心王座位置</li>
                  <li><strong>卫士：</strong>围绕国王形成十字形防守阵型</li>
                  <li><strong>攻击者：</strong>分布在棋盘四边，形成包围态势</li>
                </ul>
              </div>

              {/* 移动规则 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-purple-800">🚶 移动规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>移动方向：</strong>所有棋子只能横向或纵向直线移动</li>
                  <li><strong>移动距离：</strong>可以移动任意格数，类似国际象棋的车</li>
                  <li><strong>移动限制：</strong>不能跳过其他棋子，不能斜向移动</li>
                  <li><strong>王座规则：</strong>只有国王可以进入或停留在王座（中心点），其他棋子不可走入</li>
                  <li><strong>角落规则：</strong>只有国王可以走到角落，其他棋子不可走入角落</li>
                  <li><strong>国王特权：</strong>国王离开中心点后还可以重新走回中心点</li>
                </ul>
              </div>

              {/* 夹吃规则 */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-red-800">⚔️ 夹吃规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>普通夹吃：</strong>当敌方棋子被己方两个棋子在直线上夹住时，该敌方棋子被吃掉</li>
                  <li><strong>夹吃举例：</strong>己方棋子 - 敌方棋子 - 己方棋子 → 中间敌方棋子被吃</li>
                  <li><strong>重要规则：</strong>棋盘的角落永远相当于走棋一方的虚拟棋子，可用来夹吃任何敌方棋子</li>
                  <li><strong>夹吃举例：</strong>己方棋子 - 敌方棋子 - 角落 → 敌方棋子被吃</li>
                  <li><strong>王座规则：</strong>空王座也可以作为夹吃的支撑点</li>
                </ul>
              </div>

              {/* 国王特殊规则 */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-amber-800">👑 国王特殊规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>国王捕获：</strong>需要四面包围才能捕获国王（上下左右都有攻击者）</li>
                  <li><strong>国王移动：</strong>国王的移动规则与其他棋子相同</li>
                  <li><strong>王座特权：</strong>国王可以在王座上停留，其他棋子不能</li>
                </ul>
              </div>

              {/* 胜利条件 */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-indigo-800">🏆 胜利条件</h3>
                <div className="text-sm space-y-2">
                  <div>
                    <strong className="text-blue-600">护王方胜利条件：</strong>
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>✅ 国王成功到达任意一个角落逃脱点</li>
                      <li>✅ 吃掉所有攻击者（使其无法移动）</li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-red-600">攻击方胜利条件：</strong>
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>✅ 四面包围并捕获国王</li>
                      <li>✅ 国王无法移动到任何角落</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 游戏策略提示 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-gray-800">💡 策略提示</h3>
                <div className="text-sm space-y-2">
                  <p><strong className="text-blue-600">护王方策略：</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• 保护国王同时寻找通往角落的安全路径</li>
                    <li>• 利用卫士形成防守阵线，阻挡攻击者</li>
                    <li>• 适时牺牲卫士为国王开辟道路</li>
                  </ul>

                  <p><strong className="text-red-600">攻击方策略：</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• 快速向国王中心靠拢，形成包围圈</li>
                    <li>• 协调多个攻击者同时逼近国王</li>
                    <li>• 利用夹吃规则减少卫士数量</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowRules(false)}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div className="text-sm text-gray-300">
                © 2025 <a href="https://oho.cn" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">oho.cn</a> 版权所有
              </div>
              <div className="text-sm text-gray-300">
                <a href="http://beian.miit.gov.cn" target="_blank" rel="nofollow" className="hover:text-white transition-colors">浙ICP备13034790号-1</a>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              北欧传统棋类游戏 • 萨米板棋（Tablut）
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TablutGame;